import React, { ReactElement, FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typography } from 'antd'
import { s } from './style'
import { getAddress, getSpaceCapacity, getStatuses } from '../../../../../../Graphql-Utilities'
import { formatCents } from '../../../../../../Utilities'
import { useDateTimeDuration } from '../../../../../../Hooks'
import { ProposalModal } from '../../../../ProposalModal'
import {
  ProposalCardContentEventFragment,
  ProposalCardContentProposalFragment,
} from './ProposalCardContent.generated-types'

const { Text } = Typography

interface ProposalCardContentProps {
  event: ProposalCardContentEventFragment
  proposal: ProposalCardContentProposalFragment
}

export const ProposalCardContent: FC<ProposalCardContentProps> = ({
  event,
  event: { currency },
  proposal,
  proposal: {
    baseCostTotal,
    space: { spaceName, spacePhotos, venue },
  },
}): ReactElement => {
  const { t } = useTranslation()
  const [isProposalModalVisible, setIsProposalModalVisible] = useState(false)
  const { durationFullDate } = useDateTimeDuration(event)

  const address = getAddress(proposal)
  const spaceCapacity = getSpaceCapacity({ event, proposal })
  const canRequestToBook = getStatuses(event).isActiveWithResponses

  const handleViewDetails = (): void => {
    setIsProposalModalVisible(true)
  }

  const handleCloseProposalModal = (): void => setIsProposalModalVisible(false)

  return (
    <>
      <ProposalModal
        modalVisible={isProposalModalVisible}
        handleCloseModal={handleCloseProposalModal}
        response={proposal}
        canRequestToBook={canRequestToBook}
        event={event}
      />
      <div style={s.root}>
        {spacePhotos?.length > 0 && (
          <img src={spacePhotos[0].url} alt='Space photo' style={s.image} />
        )}
        <div style={s.column}>
          {venue && (
            <div style={s.spaceBetweenRow}>
              <div>
                <div style={s.venueSpaceRow}>
                  <Text strong>{venue.name}</Text>
                  {spaceName && <Text style={s.spaceName}>{spaceName}</Text>}
                </div>
                {address && <Text>{address}</Text>}
              </div>
            </div>
          )}
          <div style={s.row}>
            <Text strong>{t('eventHub.inProgress.response.availability', 'Availability')}</Text>
            <Text style={s.normalText}>{durationFullDate}</Text>
            {spaceCapacity && (
              <>
                <Text style={s.capacity} strong>
                  {t('eventHub.inProgress.response.capacity', 'Capacity')}
                </Text>
                <Text style={s.normalText}>{spaceCapacity}</Text>
              </>
            )}
          </div>
          <div style={s.spaceBetweenRow}>
            <div style={s.row}>
              <Text strong>
                {t('eventHub.inProgress.response.totalCost', 'Total cost (ex VAT)')}
              </Text>
              <Text style={s.normalText}>{formatCents(currency, baseCostTotal)}</Text>
            </div>
            <div style={s.buttonsGroup}>
              <Button onClick={handleViewDetails} type='link' style={s.ghostButton}>
                {t('eventHub.inProgress.response.viewDetails', 'View details')}
              </Button>
              <Button
                onClick={handleViewDetails}
                disabled={!canRequestToBook}
                type='primary'
                style={s.primaryButton}>
                {t('eventHub.inProgress.response.book', 'Book')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
